import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { KeyRound, Loader2, Smartphone } from "lucide-react";
import { api } from "../lib/api";
import { useIsMobile } from "../hooks/useIsMobile";

const TOKEN_KEY = "meyar_device_token";
const APPROVED_FLAG_KEY = "meyar_device_approved";

/**
 * Mobile viewport gate. The app targets desktop/laptop/tablet by default.
 * On a mobile viewport (<640px) we require an **approved device**:
 *   - the phone must present a device token issued by the owner-managed
 *     3-slot pool (see `/admin/devices`)
 *   - first-time setup: the owner generates a 6-digit code and the user
 *     types it into this gate once; the token lives in localStorage after
 *   - the backend re-verifies the token on every mount (not just trust
 *     the local flag) — so clearing the backend binding kicks the phone
 *     back to the activation screen
 *
 * Exceptions (always allowed through):
 *   - public discount cards at `/d/*`
 *   - non-mobile viewports (desktop/tablet)
 */
export default function MobileGate({ children }) {
    const location = useLocation();
    const isMobile = useIsMobile(640);
    const [state, setState] = useState("checking"); // checking | ok | need-code
    const [code, setCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");
    const [slotLabel, setSlotLabel] = useState("");

    useEffect(() => {
        if (!isMobile) { setState("ok"); return; }
        if (location.pathname.startsWith("/d/")) { setState("ok"); return; }
        let cancelled = false;
        (async () => {
            const token = localStorage.getItem(TOKEN_KEY);
            if (!token) { if (!cancelled) setState("need-code"); return; }
            try {
                const r = await api.post("/device/check", { token });
                if (cancelled) return;
                if (r.data?.approved) {
                    setSlotLabel(r.data.slot_label || "");
                    localStorage.setItem(APPROVED_FLAG_KEY, "1");
                    setState("ok");
                } else {
                    localStorage.removeItem(TOKEN_KEY);
                    localStorage.removeItem(APPROVED_FLAG_KEY);
                    setState("need-code");
                }
            } catch {
                if (!cancelled) setState("need-code");
            }
        })();
        return () => { cancelled = true; };
    }, [isMobile, location.pathname]);

    const submit = async () => {
        setErr("");
        const c = (code || "").trim();
        if (c.length !== 6 || !/^\d+$/.test(c)) { setErr("أدخل كوداً من 6 أرقام"); return; }
        setSubmitting(true);
        try {
            const r = await api.post("/device/claim", { code: c });
            localStorage.setItem(TOKEN_KEY, r.data.token);
            localStorage.setItem(APPROVED_FLAG_KEY, "1");
            setSlotLabel(r.data.slot_label || "");
            setState("ok");
        } catch (e) {
            setErr(e?.response?.data?.detail || "فشل الاعتماد");
        } finally { setSubmitting(false); }
    };

    if (state === "checking" && isMobile && !location.pathname.startsWith("/d/")) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-neutral-50" dir="rtl">
                <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
            </div>
        );
    }
    if (state === "need-code") {
        return (
            <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-5 py-10" dir="rtl">
                <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-sm ring-1 ring-neutral-200">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                        <Smartphone className="h-6 w-6" />
                    </div>
                    <h2 className="text-center font-heading text-xl font-bold text-neutral-900">
                        اعتماد الجهاز
                    </h2>
                    <p className="mt-2 text-center text-sm text-neutral-600">
                        هذا الجهاز غير معتمد لاستخدام معيار على الجوال. أدخل كود الاعتماد المكوَّن من 6 أرقام الذي تحصل عليه من إدارة معيار.
                    </p>
                    <div className="mt-5">
                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            maxLength={6}
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="------"
                            dir="ltr"
                            className="w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-center font-mono text-2xl font-bold tracking-[0.5em] focus:border-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-100"
                            data-testid="device-code-input"
                        />
                        {err ? (
                            <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="device-error">
                                {err}
                            </div>
                        ) : null}
                        <button
                            onClick={submit}
                            disabled={submitting || code.length !== 6}
                            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-700 py-3 text-sm font-bold text-white transition hover:bg-amber-800 disabled:cursor-not-allowed disabled:bg-neutral-300"
                            data-testid="device-submit-btn"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                            اعتماد الجهاز
                        </button>
                    </div>
                    <div className="mt-5 text-center text-[11px] text-neutral-500" data-testid="mobile-gate-note">
                        للكمبيوتر واللابتوب والتابلت لا يحتاج اعتماد.
                    </div>
                </div>
            </div>
        );
    }
    return children;
}
